/**
 * Reading the offer pulse — the JUDGEMENT half, kept out of the proxy on purpose.
 *
 * The warehouse ships counts and rates; whether "38 case interviews in a fortnight against 36 in
 * the one before" means offers are about to rise is a statement about how FINN reads its own
 * pipeline. It changes more often than the data does and it has to be testable without a network,
 * which is the same split the bottleneck radar takes for its traffic lights.
 *
 * ⚠️ THE CASE STAGE IS THE LEADING INDICATOR, and the lag is short enough for it to be useful:
 * measured against the live warehouse on 2026-09-29, the median case → offer was **6 days** (p90
 * 15) and 24% of a mature case cohort reached an offer. So a fortnight of case interviews is
 * roughly the next fortnight of offers, which is what makes this a forecast rather than a mood.
 */
import type { OfferPulse } from '../types/openPositions';

/**
 * Below this many case interviews in a window, the comparison is noise. Same constant and same
 * reasoning as the radar's `MIN_COHORT`: a rate on three candidates clears every threshold and
 * means nothing, and "we cannot say" is a better answer than a confident arrow.
 */
export const MIN_CASE_COHORT = 10;

/** Inside this band the two windows are the same week twice, not a movement. */
const DEADBAND = 0.15;

export type TrendDirection = 'up' | 'down' | 'steady' | 'unknown';

export interface OfferTrend {
  direction: TrendDirection;
  /** Offers the recent case cohort is expected to produce. Null when nothing can be said. */
  expected: number | null;
  /** The movement in the case pipeline, as a ratio of the prior window. Null when unknown. */
  ratio: number | null;
  /** One sentence, always — an arrow with no reason under it invites the wrong reading. */
  reason: string;
}

export function offerTrend(pulse: OfferPulse | null | undefined): OfferTrend {
  if (!pulse) {
    return { direction: 'unknown', expected: null, ratio: null, reason: 'The pipeline could not be read.' };
  }

  const { caseRecent, casePrior, caseToOfferRate, trendWindowDays } = pulse;
  const weeks = Math.round(trendWindowDays / 7);
  const span = `${weeks} ${weeks === 1 ? 'week' : 'weeks'}`;

  const expected =
    caseToOfferRate != null ? Math.round(caseRecent * caseToOfferRate * 10) / 10 : null;

  /*
   * ⚠️ BOTH windows have to clear the floor, not just the recent one. A jump from 2 to 12 is a
   * ratio of 6 and is very likely to be two quiet weeks followed by one normal one — reading that
   * as "offers are about to surge" is exactly the false confidence the floor exists to prevent.
   */
  if (caseRecent < MIN_CASE_COHORT || casePrior < MIN_CASE_COHORT) {
    return {
      direction: 'unknown',
      expected,
      ratio: null,
      reason: `Too few case interviews to call — ${caseRecent} in the last ${span}, ${casePrior} in the ${span} before.`,
    };
  }

  const ratio = caseRecent / casePrior;
  const pct = Math.round(Math.abs(ratio - 1) * 100);
  const direction: TrendDirection =
    ratio > 1 + DEADBAND ? 'up' : ratio < 1 - DEADBAND ? 'down' : 'steady';

  const movement =
    direction === 'steady'
      ? `Case interviews are level — ${caseRecent} in the last ${span} against ${casePrior} before.`
      : `Case interviews are ${pct}% ${direction === 'up' ? 'up' : 'down'} — ${caseRecent} in the last ${span} against ${casePrior} before.`;

  const consequence =
    expected == null
      ? ''
      : ` At our usual ${Math.round((caseToOfferRate as number) * 100)}% case-to-offer rate that is about ${expected} more ${expected === 1 ? 'offer' : 'offers'}${
          pulse.medianCaseToOfferDays != null
            ? `, typically within ${pulse.medianCaseToOfferDays} days of the case`
            : ''
        }.`;

  return { direction, expected, ratio, reason: `${movement}${consequence}` };
}
